import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Patient, } from '../../types/patient';
import { NURSE_TYPE_THEME, ROLE_COLORS as NURSE_ROLE_COLORS, ROLE_TYPES as NURSE_ROLE_TYPES } from '../../types/nurse';
import { GenderAgeDisplay, NameNicknameDisplay, TimeDisplay } from '../common';

interface BlockedNursesSectionProps {
  patient: Patient;
}

/**
 * 患者がブロックした看護師一覧を表示するセクション
 */
const BlockedNursesSection: React.FC<BlockedNursesSectionProps> = ({
  patient
}) => {
  const navigate = useNavigate();
  // ブロックしている看護師がいない場合は何も表示しない
  const blockedNurses = patient.blocked_nurses || [];
  if (!blockedNurses.length) {
    return null;
  }
  return (
    <Card
      sx={{
        mb: 4,
        border: `1px solid #e0e0e0`,
        boxShadow: `0 1px 4px rgba(0, 0, 0, 0.08)`,
        backgroundColor: '#fffde7' // 薄い黄色の背景
      }}
    >
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ color: 'rgba(0, 0, 0, 0.87)', fontWeight: 'bold' }}>
                対話を拒否している看護師
              </Typography>
              {blockedNurses.length >= 5 && (
                <Chip
                  label={`${blockedNurses.length}人`}
                  size="small"
                  variant="outlined"
                  sx={{
                    ml: 1,
                    fontWeight: 'bold',
                    color: 'rgba(0, 0, 0, 0.87)',
                    borderWidth: 1.5
                  }}
                />
              )}
            </Box>
            <Divider sx={{ mb: 2, backgroundColor: '#fff176' }}/>

            <Box
              sx={{
                mt: 2,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '20px',
                  background: `linear-gradient(to top, ${NURSE_TYPE_THEME.NORMAL.BACKGROUND}CC, ${NURSE_TYPE_THEME.NORMAL.BACKGROUND}00)`,
                  pointerEvents: 'none',
                  zIndex: 1,
                  display: 'block'
                }
              }}
            >
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 1,
                  maxHeight: '500px', // 最大高さを指定
                  overflow: 'auto', // スクロール可能に
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BACKGROUND}`,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BORDER}`,
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: `${NURSE_TYPE_THEME.NORMAL.DARK}`
                    }
                  }
                }}
              >
                <Table
                  stickyHeader
                  aria-label="対話を拒否している看護師"
                  sx={{
                    "& .MuiTableHead-root": {
                      position: "sticky",
                      top: 0,
                      zIndex: 2
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      backgroundColor: `${NURSE_TYPE_THEME.NORMAL.BACKGROUND} !important`,
                      borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                    }
                  }}
                >
                  <TableHead sx={{
                    backgroundColor: NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                    borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                  }}>
                    <TableRow>
                      <TableCell width="7%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        ロール
                      </TableCell>
                      <TableCell width="25%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        名前 / ニックネーム
                      </TableCell>
                      <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        性別（年齢）
                      </TableCell>
                      <TableCell width="15%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        受付状態
                      </TableCell>
                      <TableCell width="15%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        ブロック日時
                      </TableCell>
                      <TableCell width="32%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        {/*スペーサー*/}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {blockedNurses
                    // ブロック日時の降順（最新のブロックが上）でソート
                    .sort((a, b) => {
                      // blocked_patientsから各看護師のブロック情報を取得
                      const aBlockedInfo = patient.blocked_patients?.[a.id];
                      const bBlockedInfo = patient.blocked_patients?.[b.id];
                      // ブロック日時を取得
                      const aBlockedAt = aBlockedInfo?.created_at;
                      const bBlockedAt = bBlockedInfo?.created_at;
                      // ブロック日時がない場合は後ろに
                      if (!aBlockedAt) return 1;
                      if (!bBlockedAt) return -1;
                      // どちらもブロック日時がある場合は降順（新しい方が前）
                      return new Date(bBlockedAt).getTime() - new Date(aBlockedAt).getTime();
                    })
                    .map((nurse) => (
                      <TableRow
                        key={nurse.id}
                        hover
                        onClick={() => navigate(`/nurses/${nurse.id}`)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                          ...(nurse.resigned_at ? {
                            backgroundColor: '#fcf5f5',
                            opacity: 0.8,
                            '& td': { color: '#777' },
                          } : {})
                        }}
                      >
                        {/* ロール */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: NURSE_ROLE_COLORS[nurse.role_type as keyof typeof NURSE_ROLE_COLORS] || '#757575',
                              fontSize: '0.75rem',
                              borderLeft: `3px solid ${NURSE_ROLE_COLORS[nurse.role_type as keyof typeof NURSE_ROLE_COLORS] || '#757575'}`,
                              pl: 1,
                              py: 0.3,
                              opacity: 0.85,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {NURSE_ROLE_TYPES[nurse.role_type as keyof typeof NURSE_ROLE_TYPES] || nurse.role_type}
                          </Typography>
                        </TableCell>

                        {/* 看護師名とニックネーム */}
                        <TableCell>
                          <NameNicknameDisplay
                            name={nurse.name}
                            nickname={nurse.nickname}
                            nameFontWeight="bold"
                            isTestAccount={(nurse as any)?.is_test_account || false}
                          />
                        </TableCell>

                        {/* 性別（年齢） */}
                        <TableCell>
                          <GenderAgeDisplay
                            genderType={nurse.gender_type || ''}
                            birthday={nurse.birthday || ''}
                            fontSize="0.9rem"
                          />
                        </TableCell>

                        {/* 受付状態 */}
                        <TableCell>
                          {nurse.can_assign !== undefined ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                component="span"
                                sx={{
                                  color: nurse.can_assign ? '#2e7d32' : '#c62828',
                                  fontWeight: 'bold',
                                  fontSize: '1.2rem',
                                  mr: 0.5,
                                  ...(nurse.resigned_at ? { opacity: 0.6 } : {})
                                }}
                              >
                                {nurse.can_assign ? '○' : '×'}
                              </Typography>
                              <Typography
                                component="span"
                                sx={{
                                  color: nurse.can_assign ? '#2e7d32' : '#c62828',
                                  fontSize: '0.9rem',
                                  ...(nurse.resigned_at ? { opacity: 0.6 } : {})
                                }}
                              >
                                {nurse.can_assign ? '受付中' : '停止中'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              情報なし
                            </Typography>
                          )}
                        </TableCell>

                        {/* ブロック日時 */}
                        <TableCell>
                          {nurse.blocked_at ? (
                            <TimeDisplay
                              timestamp={nurse.blocked_at}
                              color="text.primary"
                              iconColor="action"
                              fontSize="0.9rem"
                              fontWeight="bold"
                              sx={{
                                ...(nurse.resigned_at ? { opacity: 0.6 } : {})
                              }}
                            />
                          ) : (
                            patient.blocked_patients &&
                            nurse.id &&
                            patient.blocked_patients[nurse.id] &&
                            patient.blocked_patients[nurse.id].created_at ? (
                              <TimeDisplay
                                timestamp={patient.blocked_patients[nurse.id].created_at || ''}
                                color="text.primary"
                                iconColor="action"
                                fontSize="0.9rem"
                                fontWeight="bold"
                                sx={{
                                  ...(nurse.resigned_at ? { opacity: 0.6 } : {})
                                }}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                情報なし
                              </Typography>
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
export default BlockedNursesSection;
